import type { IAutoScrollToMarkerModule } from '../types';

const AutoScrollToMarkerModule: IAutoScrollToMarkerModule = {
    observers: new Map<HTMLElement, IntersectionObserver>(),
    mutationObserver: null as MutationObserver | null,

    initialize(): void {
        this.startDomObserver();
    },

    startDomObserver(): void {
        // MutationObserver erstellen, um neue Elemente zu erkennen
        this.mutationObserver = new MutationObserver((mutations) => {
            let foundFirstMarker = false;
            
            mutations.forEach(mutation => {
                // Überspringe, wenn bereits ein Marker gefunden wurde
                if (foundFirstMarker) return;
                
                // Prüfe hinzugefügte Nodes
                mutation.addedNodes.forEach(node => {
                    // Überspringe, wenn bereits ein Marker gefunden wurde
                    if (foundFirstMarker) return;
                    
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node as HTMLElement;

                        // Prüfe ob das Element selbst das Attribut hat
                        if (element.hasAttribute && element.hasAttribute('data-autoscroll')) {
                            this.checkSingleMarker(element);
                            foundFirstMarker = true;
                            return;
                        }

                        // Prüfe auch Kindelemente
                        if (element.querySelectorAll) {
                            const childMarkers = element.querySelectorAll('[data-autoscroll]');
                            if (childMarkers.length > 0) {
                                // Nur das erste Kindelement verarbeiten
                                this.checkSingleMarker(childMarkers[0] as HTMLElement);
                                foundFirstMarker = true;
                                return;
                            }
                        }
                    }
                });
            });
        });

        // Beobachte das gesamte DOM auf Änderungen
        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    },

    checkSingleMarker(element: HTMLElement): void {
        const scrollType = element.getAttribute('data-autoscroll') || 'atonce';
        const scrollToTop = element.getAttribute('data-scroll-to-top') === 'true';
        const scrollPosition = element.getAttribute('data-scroll-position') || 'start';
        this.scrollToMarker(element, scrollType, scrollToTop, scrollPosition);

    },

    findScrollableParent(element: HTMLElement): HTMLElement | null {
        let parent = element.parentElement;

        while (parent) {
            const overflowY = window.getComputedStyle(parent).overflowY;
            const isScrollable = overflowY === 'auto' || overflowY === 'scroll';

            // Prüfe ob das Element tatsächlich scrollbar ist (scrollHeight > clientHeight)
            if (isScrollable && parent.scrollHeight > parent.clientHeight) {
                return parent;
            }

            parent = parent.parentElement;
        }

        // Fallback: document.documentElement (html) oder document.body
        return document.documentElement;
    },

    scrollToMarker(element: HTMLElement, scrollType: string, scrollToTop: boolean, scrollPosition: string): void {
        const behavior = scrollType === 'smooth' ? 'smooth' : 'auto';
        const blockPosition = scrollPosition as ScrollLogicalPosition;

        if (scrollToTop) {
            // Finde das nächste scrollbare Parent-Element und scrolle es nach oben
            const scrollableParent = this.findScrollableParent(element);
            if (scrollableParent) {
                scrollableParent.scrollTo({
                    top: 0,
                    behavior: behavior
                });
            }
        } else {
            // Zum Element scrollen mit der angegebenen Position
            element.scrollIntoView({
                behavior: behavior,
                block: blockPosition,
                inline: 'nearest'
            });
        }
    },

    dispose(): void {
        // MutationObserver aufräumen
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }

        // Alle IntersectionObserver aufräumen
        this.observers.forEach((observer, element) => {
            observer.unobserve(element);
            observer.disconnect();
        });
        this.observers.clear();
    },

    reinitialize(): void {
        this.dispose();
        this.startDomObserver();
    }
};

export default AutoScrollToMarkerModule;